import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import * as React from "react";

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
  selectClass,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import {
  attachValidator,
  detachValidator,
  updateValidator,
  updateVersion,
} from "@/lib/api/endpoints";
import { useAttachedValidators, useValidatorCatalog, useVersion } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { FailureBehavior, ValidatorAttachment, ValidatorCatalog } from "@/types/api";

import {
  ACTION_HINT,
  ACTION_LABEL,
  HAS_SETTINGS,
  configToForm,
  formToConfig,
  type ConfigForm,
} from "./guardrails";

const DEFAULT_SAFE_MESSAGE = "Sorry, I can't help with that request.";

/** Guardrails for one version (AVI-173/174/176): which checks run on the user's
 *  message and on the answer, what each does when it triggers, and the reply a
 *  blocked user gets. Saved per version, so a clone carries them and the
 *  version diff shows changes. */
export function GuardrailsCard({ agentSlug, label }: { agentSlug: string; label: string }) {
  const catalog = useValidatorCatalog();
  const attached = useAttachedValidators(agentSlug, label);

  if (catalog.isLoading || attached.isLoading) return <Skeleton className="h-64 w-full" />;
  if (catalog.isError)
    return <ErrorState error={catalog.error} onRetry={() => void catalog.refetch()} />;
  if (attached.isError)
    return <ErrorState error={attached.error} onRetry={() => void attached.refetch()} />;

  const byslug = new Map((attached.data ?? []).map((a) => [a.validator_slug, a]));
  const checks = catalog.data ?? [];
  const input = checks.filter((c) => c.stage === "input");
  const output = checks.filter((c) => c.stage === "output");
  const other = checks.filter((c) => !c.stage);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Guardrails</CardTitle>
        <CardDescription>
          Checks on the user&rsquo;s message and on every answer. Each one can Block, Redact or
          Warn. New versions start with the recommended set.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <SafeMessage agentSlug={agentSlug} label={label} />
        <Section
          title="Input checks"
          hint="Run on the user's message before the cache and before the model is called."
          checks={input}
          attached={byslug}
          agentSlug={agentSlug}
          label={label}
        />
        <Section
          title="Output checks"
          hint="Run on every answer, cached answers included. When one can block or redact, the answer is held back until it has passed."
          checks={output}
          attached={byslug}
          agentSlug={agentSlug}
          label={label}
        />
        {other.length > 0 ? (
          <Section
            title="Other validators"
            hint="Older validators: they retry the model, then fail the run."
            checks={other}
            attached={byslug}
            agentSlug={agentSlug}
            label={label}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function SafeMessage({ agentSlug, label }: { agentSlug: string; label: string }) {
  const qc = useQueryClient();
  const version = useVersion(agentSlug, label);
  const saved = version.data?.guardrail_message ?? "";
  const [text, setText] = React.useState(saved);
  React.useEffect(() => setText(saved), [saved]);

  const save = useMutation({
    mutationFn: () =>
      updateVersion(agentSlug, label, { guardrail_message: text.trim() ? text.trim() : null }),
    onSuccess: async () => {
      toast.success("Safe message saved.");
      await qc.invalidateQueries({ queryKey: qk.version(agentSlug, label) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-1.5">
      <Label htmlFor="g-safe">Safe message</Label>
      <div className="flex gap-2">
        <Input
          id="g-safe"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={DEFAULT_SAFE_MESSAGE}
          maxLength={2000}
        />
        <Button
          variant="outline"
          disabled={save.isPending || text.trim() === saved.trim()}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        What the user sees when a check set to Block stops their message or the answer.
      </p>
    </div>
  );
}

function Section({
  title,
  hint,
  checks,
  attached,
  agentSlug,
  label,
}: {
  title: string;
  hint: string;
  checks: ValidatorCatalog[];
  attached: Map<string, ValidatorAttachment>;
  agentSlug: string;
  label: string;
}) {
  if (checks.length === 0) return null;
  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <div className="divide-y divide-border rounded-md border border-border">
        {checks.map((c) => (
          <CheckRow
            key={c.slug}
            check={c}
            attachment={attached.get(c.slug)}
            agentSlug={agentSlug}
            label={label}
          />
        ))}
      </div>
    </section>
  );
}

function CheckRow({
  check,
  attachment,
  agentSlug,
  label,
}: {
  check: ValidatorCatalog;
  attachment: ValidatorAttachment | undefined;
  agentSlug: string;
  label: string;
}) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = React.useState(false);
  const on = !!attachment;
  const actions = check.actions ?? [];
  const action = (attachment?.failure_behavior ??
    check.default_action ??
    "block") as FailureBehavior;

  const refresh = () => qc.invalidateQueries({ queryKey: qk.attachedValidators(agentSlug, label) });
  const onError = (e: Error) => toast.error(e.message);

  const toggle = useMutation({
    mutationFn: async (enable: boolean): Promise<void> => {
      if (enable) await attachValidator(agentSlug, label, { validator_slug: check.slug });
      else await detachValidator(agentSlug, label, check.slug);
    },
    onSuccess: refresh,
    onError,
  });
  const patch = useMutation({
    mutationFn: (input: { failure_behavior?: FailureBehavior; max_retries?: number }) =>
      updateValidator(agentSlug, label, check.slug, input),
    onSuccess: refresh,
    onError,
  });

  return (
    <div className="space-y-2 p-3">
      <div className="flex flex-wrap items-start gap-3">
        <input
          type="checkbox"
          aria-label={`Use ${check.name}`}
          className="mt-1 h-4 w-4 rounded border-input accent-primary"
          checked={on}
          disabled={toggle.isPending}
          onChange={(e) => toggle.mutate(e.target.checked)}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{check.name}</span>
            {check.default_on ? (
              <Badge variant="outline" className="text-[10px]">
                recommended
              </Badge>
            ) : null}
          </div>
          {check.description ? (
            <p className="text-xs text-muted-foreground">{check.description}</p>
          ) : null}
        </div>
        {on && actions.length > 0 ? (
          <div className="w-44">
            <select
              aria-label={`${check.name} action`}
              className={selectClass}
              value={action}
              disabled={patch.isPending || actions.length === 1}
              onChange={(e) =>
                patch.mutate({ failure_behavior: e.target.value as FailureBehavior })
              }
            >
              {actions.map((a) => (
                <option key={a} value={a}>
                  {ACTION_LABEL[a]}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {on ? (
        <div className="space-y-2 pl-7">
          <p className="text-xs text-muted-foreground">{ACTION_HINT[action]}</p>
          {action === "inject_correction" ? (
            <div className="flex items-center gap-2 text-xs">
              <Label htmlFor={`g-retries-${check.slug}`} className="text-xs">
                Retries
              </Label>
              <Input
                id={`g-retries-${check.slug}`}
                type="number"
                min={0}
                max={5}
                className="h-8 w-20"
                defaultValue={attachment?.max_retries ?? 0}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isInteger(n) && n >= 0 && n <= 5 && n !== attachment?.max_retries)
                    patch.mutate({ max_retries: n });
                }}
              />
            </div>
          ) : null}
          {HAS_SETTINGS.has(check.slug) ? (
            <div>
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-primary"
                onClick={() => setExpanded((x) => !x)}
              >
                {expanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                Settings
              </button>
              {expanded && attachment ? (
                <SettingsEditor
                  slug={check.slug}
                  attachment={attachment}
                  agentSlug={agentSlug}
                  label={label}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SettingsEditor({
  slug,
  attachment,
  agentSlug,
  label,
}: {
  slug: string;
  attachment: ValidatorAttachment;
  agentSlug: string;
  label: string;
}) {
  const qc = useQueryClient();
  const [form, setForm] = React.useState<ConfigForm>(() => configToForm(attachment.config));
  const set = (patch: Partial<ConfigForm>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () => {
      const built = formToConfig(slug, form);
      if ("error" in built) throw new Error(built.error);
      return updateValidator(agentSlug, label, slug, { config: built.config });
    },
    onSuccess: async () => {
      toast.success("Settings saved.");
      await qc.invalidateQueries({ queryKey: qk.attachedValidators(agentSlug, label) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="mt-2 space-y-3 rounded-md bg-muted/40 p-3">
      {slug === "input-length" ? (
        <div className="space-y-1.5">
          <Label htmlFor="g-max">Maximum characters</Label>
          <Input
            id="g-max"
            type="number"
            min={1}
            className="w-40"
            value={form.maxChars}
            onChange={(e) => set({ maxChars: e.target.value })}
          />
        </div>
      ) : null}
      {slug === "prompt-injection" ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input accent-primary"
            checked={form.llmCheck}
            onChange={(e) => set({ llmCheck: e.target.checked })}
          />
          Also ask the model (one extra call per message, catches rephrased attempts)
        </label>
      ) : null}
      {slug === "topic-scope" ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="g-topics">Allowed topics</Label>
            <Textarea
              id="g-topics"
              rows={4}
              value={form.topics}
              onChange={(e) => set({ topics: e.target.value })}
              placeholder={"Billing and invoices\nOrder status\nRefunds"}
            />
            <p className="text-xs text-muted-foreground">
              One per line. With none listed, this check does nothing.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="g-decline">Decline message</Label>
            <Input
              id="g-decline"
              value={form.declineMessage}
              onChange={(e) => set({ declineMessage: e.target.value })}
              placeholder="Empty uses the safe message"
            />
          </div>
        </>
      ) : null}
      {slug === "data-leakage" ? (
        <div className="space-y-1.5">
          <Label htmlFor="g-restricted">Restricted fields</Label>
          <Textarea
            id="g-restricted"
            rows={3}
            value={form.restrictedFields}
            onChange={(e) => set({ restrictedFields: e.target.value })}
            placeholder={"salary\ndate_of_birth"}
          />
          <p className="text-xs text-muted-foreground">
            Column names whose values must never reach the user. Personal data (emails, cards, SSNs,
            phones) is always checked.
          </p>
        </div>
      ) : null}
      {slug === "output-format" ? (
        <div className="space-y-1.5">
          <Label htmlFor="g-schema">JSON Schema the answer must match</Label>
          <Textarea
            id="g-schema"
            rows={6}
            className="font-mono text-xs"
            value={form.schema}
            onChange={(e) => set({ schema: e.target.value })}
          />
        </div>
      ) : null}
      <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}
